// src/lib/dxf.js
// ───────────────────────────────────────────────────────────────────────────
// 📐 Generador DXF de retales (PLAN-INVENTARIO-RETALES §5)
// Función PURA: rectángulo aprovechable en PULGADAS, UNA capa de corte.
//
// ⚠️ FORMATO: DXF R12 COMPLETO (AC1009). La primera versión emitía un DXF
// demasiado mínimo (sin tablas LTYPE/LAYER, coords enteras, AC1015) → ArtiosCAD
// daba ODAERR e Illustrator error [2067]. Los tests "pasaban" porque validaban
// contra el parser CASERO de dielines.js (laxo), no contra un lector real.
// Ahora el archivo se valida contra la librería real `dxf` (ver dxf.test.js) y
// debe abrir limpio en ArtiosCAD + Illustrator.
//
// Estructura R12 mínima VÁLIDA:
//   HEADER  : $ACADVER=AC1009, $INSUNITS=1 (pulgadas), $EXTMIN/$EXTMAX.
//   TABLES  : LTYPE (CONTINUOUS) + LAYER (define "0" y "CUT" color 1).
//   ENTITIES: 4 × LINE (R12 NO soporta LWPOLYLINE) en capa CUT, color 1,
//             coordenadas como REALES (0.0).
//   EOF.
//
// Vive en cliente para descarga instantánea (Blob): geometría sin costos ni
// secretos. Promovible a functions/lib/ si el servidor lo necesita.
// ───────────────────────────────────────────────────────────────────────────

/** Color índice DXF de la capa de corte (rojo = cut, colorMap de dielines.js). */
export const DXF_CUT_COLOR = 1;

/** Nombre de la capa de corte en el DXF. */
export const DXF_CUT_LAYER = 'CUT';

// Número DXF como REAL (con punto decimal). R12 exige reales en coordenadas:
// "12" → "12.0", "12.125" → "12.125", "0" → "0.0". Sin notación científica.
const real = (v) => {
    let s = String(Math.round(Number(v) * 1e6) / 1e6);
    if (!/[.eE]/.test(s)) s += '.0';
    return s;
};

/**
 * Construye un DXF R12 (AC1009) con un rectángulo cerrado de (0,0) a (w,h).
 * - Unidades: PULGADAS ($INSUNITS = 1).
 * - Una capa de corte (CUT, color 1 = rojo = cut) + capa 0 definida.
 * - 4 × LINE (R12 no soporta LWPOLYLINE). Sin crease, marcas ni texto (MVP §5).
 *
 * @param {object} opts
 * @param {number} opts.widthIn   Medida 1 en pulgadas (cartón = dirección de flauta, §5b)
 * @param {number} opts.heightIn  Medida 2 en pulgadas
 * @param {number} [opts.color]   índice de color DXF de la capa de corte (default 1)
 * @return {string} contenido DXF ASCII (R12)
 */
export function buildRectangleDxf({ widthIn, heightIn, color = DXF_CUT_COLOR } = {}) {
    const w = Number(widthIn);
    const h = Number(heightIn);
    if (!Number.isFinite(w) || w <= 0 || !Number.isFinite(h) || h <= 0) {
        throw new Error('buildRectangleDxf: widthIn y heightIn deben ser números > 0 (pulgadas).');
    }

    const L = [];
    const p = (...vals) => L.push(...vals);

    // ── HEADER ──
    p('0', 'SECTION', '2', 'HEADER');
    p('9', '$ACADVER', '1', 'AC1009');          // R12
    p('9', '$INSUNITS', '70', '1');             // 1 = pulgadas
    p('9', '$INSBASE', '10', '0.0', '20', '0.0', '30', '0.0');
    p('9', '$EXTMIN', '10', '0.0', '20', '0.0', '30', '0.0');
    p('9', '$EXTMAX', '10', real(w), '20', real(h), '30', '0.0');
    p('0', 'ENDSEC');

    // ── TABLES: LTYPE (CONTINUOUS) + LAYER (0 + CUT) ──
    p('0', 'SECTION', '2', 'TABLES');
    p('0', 'TABLE', '2', 'LTYPE', '70', '1');
    p('0', 'LTYPE', '2', 'CONTINUOUS', '70', '0', '3', 'Solid line', '72', '65', '73', '0', '40', '0.0');
    p('0', 'ENDTAB');
    p('0', 'TABLE', '2', 'LAYER', '70', '2');
    p('0', 'LAYER', '2', '0', '70', '0', '62', '7', '6', 'CONTINUOUS');                 // capa 0 (blanco)
    p('0', 'LAYER', '2', DXF_CUT_LAYER, '70', '0', '62', String(color), '6', 'CONTINUOUS'); // CUT (rojo=1)
    p('0', 'ENDTAB');
    p('0', 'ENDSEC');

    // ── ENTITIES: 4 × LINE cerrando el rectángulo, en capa CUT, color 1 ──
    p('0', 'SECTION', '2', 'ENTITIES');
    const segs = [
        [0, 0, w, 0],
        [w, 0, w, h],
        [w, h, 0, h],
        [0, h, 0, 0],
    ];
    for (const [x1, y1, x2, y2] of segs) {
        p('0', 'LINE', '8', DXF_CUT_LAYER, '62', String(color));
        p('10', real(x1), '20', real(y1), '30', '0.0');
        p('11', real(x2), '21', real(y2), '31', '0.0');
    }
    p('0', 'ENDSEC');
    p('0', 'EOF');

    // DXF tradicional: líneas separadas por CRLF (máxima compatibilidad de lectores).
    return L.join('\r\n') + '\r\n';
}

/** Descarga client-side del DXF como archivo (Blob). Sin servidor: UX instantánea. */
export function downloadDxf(dxfText, filename) {
    const blob = new Blob([dxfText], { type: 'application/dxf' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
}
